import { useState, type CSSProperties } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useLiveQuery } from 'dexie-react-hooks'
import {
  DndContext, closestCenter, MouseSensor, TouchSensor,
  useSensor, useSensors, type DragEndEvent,
} from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { DIAS_SEMANA } from '../../db/tipos'
import type { Cliente, Visita } from '../../db/tipos'
import { obterOuCriarPedidoAberto } from '../../pedidos/carrinho'
import {
  rotaDoDia, ultimaCompraPorCliente, visitasDeHoje,
  diasDesde, registrarVisita, desfazerVisita, remarcarRetorno,
} from '../../visitas/logica'
import { somarDias } from '../../visitas/retorno'
import { dataISOLocal } from '../../pedidos/precos'
import { urlGoogleMaps, urlWaze } from '../../visitas/navegacao'
import { lerOrdem, gravarOrdem, ordenarPorPreferencia, moverNaLista, reordenar } from '../../visitas/ordem'
import {
  situacaoDaVisita, rotuloDaVisita, ROTULO_MOTIVO, MOTIVOS_SEM_PEDIDO,
  CLASSE_CARTAO_SITUACAO, CLASSE_SELO_SITUACAO, CLASSE_NUMERO_SITUACAO, type MotivoSemPedido,
} from '../../visitas/situacao'
import { banco } from '../../db/banco'
import { formatarMoeda } from '../../lib/formatos'
import { resumoDeCompras, linkDeLigar, type ResumoDeCompras } from '../../visitas/resumoDaParada'

// Ciclo 2B: "Nao visitei / remarquei" e opcao da TELA, nao motivo de visita --
// nao entra no enum do banco.
type MotivoDaTela = MotivoSemPedido | 'remarquei'

export default function AgendaDia() {
  const hoje = new Date()
  const diaDeHoje = hoje.getDay()

  const [diaMostrado, setDiaMostrado] = useState(diaDeHoje)
  const [ordem, setOrdem] = useState<string[]>(() => lerOrdem(diaDeHoje))
  const [checkinAberto, setCheckinAberto] = useState<string | null>(null)
  const [motivo, setMotivo] = useState<MotivoDaTela>('fechado')
  const [observacao, setObservacao] = useState('')
  const [dataRetorno, setDataRetorno] = useState('')

  // useLiveQuery em TUDO que vem do banco: leitura unica no useEffect mente em
  // aparelho recem-aberto, porque o dado do servidor chega depois. Ja custou
  // quatro telas neste projeto.
  //
  // As funcoes da Task 2 sao chamadas AQUI DENTRO de proposito: o Dexie observa
  // as tabelas lidas dentro do callback, entao a tela continua ao vivo E passa a
  // rodar exatamente o codigo que os testes de unidade cobrem. Repetir a
  // consulta aqui criaria uma segunda copia da mesma regra, que um dia diverge.
  // Ciclo 2B: a rota do dia = retornos vencidos (topo) + clientes do dia da
  // semana sem os que ja estao no topo. Mesma funcao do card do Inicio.
  const rota = useLiveQuery(() => rotaDoDia(diaMostrado, new Date()), [diaMostrado])
  const ultimaCompraLive = useLiveQuery(() => ultimaCompraPorCliente(), [])
  const visitasLive = useLiveQuery(() => visitasDeHoje(), [])
  // Print dela 08/10: valor da ultima compra e quantos pedidos. Le pedidos E
  // sub_pedidos dentro da consulta viva -- os dois chegam em tempos diferentes
  // na sincronizacao.
  const resumoLive = useLiveQuery(async () =>
    resumoDeCompras(await banco.pedidos.toArray(), await banco.sub_pedidos.toArray()), [])

  const ehHoje = diaMostrado === diaDeHoje

  // Tipo explicito, NAO `typeof`: o tipo declarado do useLiveQuery inclui
  // undefined, e `typeof` devolve o declarado, nao o estreitado.
  const ultimaCompra: Map<string, string> = ultimaCompraLive ?? new Map()
  const visitaPorCliente: Map<string, Visita> = visitasLive ?? new Map()
  const resumo: Map<string, ResumoDeCompras> = resumoLive ?? new Map()

  const retornos = rota?.retornos ?? []
  const ordenados = ordenarPorPreferencia(rota?.doDia ?? [], ordem)
  const visitados = [...retornos.map((r) => r.cliente), ...ordenados]
    .filter((c) => visitaPorCliente.has(c.id)).length
  const naRota = retornos.length + ordenados.length
  const clientes = (n: number) => `${n} ${n === 1 ? 'cliente' : 'clientes'} na rota`

  // ⚠ OS NUMEROS SAO A DECISAO, nao ajuste fino. 500ms para levantar, e
  // tolerancia de 5px: se o dedo ANDAR mais que isso durante o meio segundo, e
  // rolagem, e nada levanta. Sem a tolerancia, rolar a lista devagar com o dedo
  // quase parado levantaria um card sem querer -- e o gesto que ela escolheu
  // ficaria pior do que as setas.
  //
  // ⚠ MouseSensor, e NUNCA PointerSensor: o PointerSensor escuta `pointerdown`,
  // que o DEDO tambem dispara -- e antes do `touchstart`. Os dois sensores
  // ficariam pendurados no mesmo card e o do ponteiro ganharia no celular, com
  // seus 8px e sem atraso nenhum: rolar a lista levantaria card. Conferido no
  // codigo do dnd-kit, que so filtra `isPrimary` e o botao, nunca o pointerType.
  const sensores = useSensors(
    useSensor(TouchSensor, { activationConstraint: { delay: 500, tolerance: 5 } }),
    // Mouse (desktop e E2E): sem atraso, ativa depois de 8px de arrasto.
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
  )

  function andarDia(passo: -1 | 1) {
    const novo = (diaMostrado + passo + 7) % 7
    setDiaMostrado(novo)
    setOrdem(lerOrdem(novo))
    setCheckinAberto(null)
  }

  function mover(clienteId: string, direcao: -1 | 1) {
    const nova = moverNaLista(ordenados.map((c) => c.id), clienteId, direcao)
    if (!nova) return
    gravarOrdem(diaMostrado, nova)
    setOrdem(nova)
  }

  function aoSoltar(evento: DragEndEvent) {
    const { active, over } = evento
    if (!over || active.id === over.id) return
    const ids = ordenados.map((c) => c.id)
    // reordenar mora em visitas/ordem.ts e tem teste proprio; arrayMove do
    // dnd-kit faria o mesmo, mas a regra do app fica no modulo do app.
    const nova = reordenar(ids, ids.indexOf(String(active.id)), ids.indexOf(String(over.id)))
    gravarOrdem(diaMostrado, nova)
    setOrdem(nova)
  }

  // Revisao final: o formulario do "Nao comprou" e um so para todos os cartoes.
  // Abrir outro cartao zera motivo, observacao e data -- senao a data digitada
  // no cartao A remarcaria o cliente B.
  function abrirCheckin(id: string | null) {
    setCheckinAberto(id)
    setMotivo('fechado')
    setObservacao('')
    setDataRetorno('')
  }

  async function confirmarNaoComprou(cliente: Cliente) {
    // "Nao visitei / remarquei" NAO grava visita (decisao dela 06/10): so a data.
    if (motivo !== 'remarquei') {
      await registrarVisita(cliente.id, 'sem_pedido', motivo, observacao || null)
    }
    if (dataRetorno) await remarcarRetorno(cliente.id, dataRetorno)
    setCheckinAberto(null)
    setObservacao('')
    setMotivo('fechado')
    setDataRetorno('')
  }

  return (
    <section className="p-4 space-y-3">
      {/* Print dela 08/10: a aba virou "Rota". Sem km nem tempo -- o app nao
          calcula percurso, e ela disse que nao precisa se fosse grande demais. */}
      <div className="flex items-center justify-between gap-2">
        <button onClick={() => andarDia(-1)} aria-label="Dia anterior" className="h-10 w-10 shrink-0 rounded-full border border-slate-200 bg-white text-xl text-slate-600 shadow-sm">‹</button>
        <div className="text-center min-w-0">
          <h2 className="text-xl font-bold text-slate-900">
            Rota de {DIAS_SEMANA[diaMostrado].toLowerCase()}
          </h2>
          {ehHoje ? (
            <span data-testid="contagem-dia" className="text-xs text-slate-500">
              {clientes(naRota)} · {visitados} de {naRota} visitados
            </span>
          ) : (
            <span className="text-xs text-slate-500">{clientes(naRota)} · planejamento, check-in só no dia</span>
          )}
        </div>
        <button onClick={() => andarDia(1)} aria-label="Próximo dia" className="h-10 w-10 shrink-0 rounded-full border border-slate-200 bg-white text-xl text-slate-600 shadow-sm">›</button>
      </div>

      <div className="flex items-center gap-3 rounded-2xl border border-teal-100 bg-teal-50/60 p-2.5">
        <MiniMapa />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-teal-900">Sua rota do dia</p>
          <p className="text-xs text-slate-600">{naRota} {naRota === 1 ? 'visita' : 'visitas'}</p>
        </div>
        <Link
          to="/agenda/montar"
          className="shrink-0 rounded-xl bg-teal-800 px-3 py-2 text-xs font-semibold text-white"
        >
          Montar rota da semana ›
        </Link>
      </div>

      {ordenados.length === 0 && retornos.length === 0 && (
        <p className="text-sm text-slate-500">
          Nenhum cliente na rota de {DIAS_SEMANA[diaMostrado].toLowerCase()}. Toque em
          "Montar rota da semana" para escolher quem você visita nesse dia.
        </p>
      )}

      {retornos.length > 0 && (
        <div data-testid="bloco-retorno" className="space-y-2">
          <h3 className="text-sm font-bold text-slate-700">Retorno</h3>
          {retornos.map((r, i) => (
            <CartaoDaParada
              key={r.cliente.id}
              cliente={r.cliente}
              indice={i}
              ehHoje={ehHoje}
              // Decisao dela 06/10: visitado hoje, o atraso nao existe mais --
              // o selo vira "Retorno feito hoje" (verde) e o cartao fica ate amanha.
              selo={ehHoje && visitaPorCliente.has(r.cliente.id)
                ? 'Retorno feito hoje'
                : r.diasDeAtraso > 0 ? `Retorno atrasado há ${r.diasDeAtraso} dia(s)` : 'Retorno'}
              tomDoSelo={ehHoje && visitaPorCliente.has(r.cliente.id) ? 'feito' : r.diasDeAtraso > 0 ? 'atrasado' : 'normal'}
              arrastavel={false}
              visita={ehHoje ? visitaPorCliente.get(r.cliente.id) : undefined}
              dias={diasDesde(ultimaCompra.get(r.cliente.id) ?? null, hoje)}
              resumo={resumo.get(r.cliente.id)}
              checkinAberto={checkinAberto}
              setCheckinAberto={abrirCheckin}
              motivo={motivo}
              setMotivo={setMotivo}
              observacao={observacao}
              setObservacao={setObservacao}
              dataRetorno={dataRetorno}
              setDataRetorno={setDataRetorno}
              aoMover={() => {}}
              aoConfirmarNaoComprou={confirmarNaoComprou}
            />
          ))}
          {ordenados.length > 0 && (
            <h3 className="text-sm font-bold text-slate-700 pt-2">
              Rota de {DIAS_SEMANA[diaMostrado].toLowerCase()}
            </h3>
          )}
        </div>
      )}

      <DndContext sensors={sensores} collisionDetection={closestCenter} onDragEnd={aoSoltar}>
        <SortableContext items={ordenados.map((c) => c.id)} strategy={verticalListSortingStrategy}>
          {ordenados.map((cliente, indice) => (
            <CartaoDaParada
              key={cliente.id}
              cliente={cliente}
              indice={indice}
              ehHoje={ehHoje}
              // Fora do dia de hoje a tela e so planejamento: sem cor, sem check-in.
              visita={ehHoje ? visitaPorCliente.get(cliente.id) : undefined}
              dias={diasDesde(ultimaCompra.get(cliente.id) ?? null, hoje)}
              resumo={resumo.get(cliente.id)}
              checkinAberto={checkinAberto}
              setCheckinAberto={abrirCheckin}
              motivo={motivo}
              setMotivo={setMotivo}
              observacao={observacao}
              setObservacao={setObservacao}
              dataRetorno={dataRetorno}
              setDataRetorno={setDataRetorno}
              aoMover={mover}
              aoConfirmarNaoComprou={confirmarNaoComprou}
            />
          ))}
        </SortableContext>
      </DndContext>
    </section>
  )
}

const CLASSE_TOM_DO_SELO = {
  normal: 'bg-amber-100 text-amber-800',
  atrasado: 'bg-red-100 text-red-700',
  feito: 'bg-green-100 text-green-800',
} as const

type PropsCartao = {
  cliente: Cliente
  indice: number
  ehHoje: boolean
  visita: Visita | undefined
  dias: number | null
  resumo: ResumoDeCompras | undefined
  checkinAberto: string | null
  setCheckinAberto: (id: string | null) => void
  motivo: MotivoDaTela
  setMotivo: (motivo: MotivoDaTela) => void
  observacao: string
  setObservacao: (texto: string) => void
  dataRetorno: string
  setDataRetorno: (data: string) => void
  // Ciclo 2B: cartao do bloco Retorno -- com selo e sem arrastar (a ordem e a do atraso).
  selo?: string
  tomDoSelo?: keyof typeof CLASSE_TOM_DO_SELO
  arrastavel?: boolean
  aoMover: (clienteId: string, direcao: -1 | 1) => void
  aoConfirmarNaoComprou: (cliente: Cliente) => void
}

// O cartao virou componente proprio por uma razao so: useSortable e um hook, e
// hook nao roda dentro de um map. O conteudo e o mesmo de antes.
function CartaoDaParada({
  cliente, indice, ehHoje, visita, dias, resumo,
  checkinAberto, setCheckinAberto, motivo, setMotivo, observacao, setObservacao,
  dataRetorno, setDataRetorno, selo, tomDoSelo = 'normal', arrastavel = true,
  aoMover, aoConfirmarNaoComprou,
}: PropsCartao) {
  const navegar = useNavigate()
  // ⚠ O `attributes` do useSortable NAO e pego de proposito. Ele poe
  // role="button" no card, e o nome acessivel de um role=button vem do conteudo
  // -- o cartao inteiro viraria um botao chamado "... ✓ Nao comprou", e o
  // getByRole do e2e da agenda passaria a casar DOIS elementos. Alem disso e
  // botao dentro de botao. Nada se perde: `attributes` so carrega acessibilidade
  // de teclado, e o caminho de teclado aqui sao as setas ▲▼, que ficam intactas.
  const { listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: cliente.id, disabled: !arrastavel })

  // Data marcada sempre no futuro; no "remarquei" ela e obrigatoria.
  const amanhaISO = somarDias(dataISOLocal(new Date()), 1)
  const dataValida = dataRetorno !== '' && dataRetorno >= amanhaISO
  // Decisao dela 06/10: o cliente da rota da semana remarcado nao sai da rota --
  // ganha o selo com a nova data (sem ele, o "Remarcar" parecia nao ter feito nada).
  const remarcadoPara = arrastavel && !visita && (cliente.retorno_marcado ?? '') >= amanhaISO
    ? cliente.retorno_marcado!.split('-').reverse().slice(0, 2).join('/')
    : null
  const podeConfirmar = motivo === 'remarquei' ? dataValida : dataRetorno === '' || dataValida

  const situacao = situacaoDaVisita(visita)
  const rotulo = rotuloDaVisita(visita)
  const mapsUrl = urlGoogleMaps(cliente)
  const wazeUrl = urlWaze(cliente)
  const ligarUrl = linkDeLigar(cliente)

  // Sombra e reducao enquanto levantado: sem retorno visual ela nao sabe se o
  // card saiu do lugar, e no sol isso e pior ainda.
  //
  // touchAction 'manipulation' e userSelect 'none' NAO sao enfeite: sem eles o
  // meio segundo de dedo parado no Android vira selecao de texto e lupa em cima
  // do card, justamente durante o gesto. 'manipulation' continua deixando ROLAR
  // a lista com o dedo -- so tira o duplo-toque de zoom.
  const estilo: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.85 : 1,
    boxShadow: isDragging ? '0 8px 20px rgba(0,0,0,0.25)' : undefined,
    position: 'relative',
    zIndex: isDragging ? 10 : undefined,
    touchAction: 'manipulation',
    userSelect: 'none',
    WebkitTouchCallout: 'none',
  }

  return (
    <div
      ref={setNodeRef}
      style={estilo}
      {...(arrastavel ? listeners : {})}
      data-testid={arrastavel ? `cartao-${cliente.id}` : `retorno-${cliente.id}`}
      className={`rounded-2xl shadow-sm p-3 space-y-2.5 border-l-4 ${CLASSE_CARTAO_SITUACAO[situacao]}`}
    >
      <div className="flex items-start gap-2.5">
        {/* Decisao dela 08/10: a cor do numero diz a situacao da visita. */}
        <div className="flex flex-col items-center shrink-0">
          <span
            data-testid="numero-parada"
            className={`h-9 w-9 rounded-full text-white font-bold flex items-center justify-center ${CLASSE_NUMERO_SITUACAO[situacao]}`}
          >
            {indice + 1}
          </span>
          {arrastavel && (
            <>
              <button onClick={() => aoMover(cliente.id, -1)} aria-label="Subir" className="text-slate-500 text-xs leading-none p-1.5">▲</button>
              <button onClick={() => aoMover(cliente.id, 1)} aria-label="Descer" className="text-slate-500 text-xs leading-none p-1.5">▼</button>
            </>
          )}
        </div>
        <div className="flex-1 min-w-0 space-y-1.5">
          <div className="flex items-start justify-between gap-2">
            <span className="block text-[15px] font-bold leading-tight text-slate-900 break-words min-w-0">
              {cliente.nome_fantasia || cliente.razao_social}
            </span>
            {/* Bug dela 06/10 (& L C): visita COM pedido nao se desfaz por um
                toque -- o selo e so leitura. Sai se o pedido for cancelado. */}
            {visita?.resultado === 'com_pedido' && (
              <span className={`text-xs rounded px-2 py-0.5 shrink-0 ${CLASSE_SELO_SITUACAO[situacao]}`}>
                {rotulo}
              </span>
            )}
            {visita && visita.resultado !== 'com_pedido' && (
              <button
                onClick={() => void desfazerVisita(cliente.id)}
                title="Toque para desfazer"
                className={`text-xs rounded px-2 py-0.5 shrink-0 ${CLASSE_SELO_SITUACAO[situacao]}`}
              >
                {rotulo} ✕
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-1">
            {selo && (
              <span
                data-testid="selo-retorno"
                className={`inline-block text-xs rounded px-2 py-0.5 font-semibold ${CLASSE_TOM_DO_SELO[tomDoSelo]}`}
              >
                {selo}
              </span>
            )}
            {remarcadoPara && (
              <span
                data-testid="selo-remarcado"
                className="inline-block text-xs rounded px-2 py-0.5 font-semibold bg-sky-100 text-sky-800"
              >
                Remarcado p/ {remarcadoPara}
              </span>
            )}
            {dias !== null ? (
              <span className="inline-flex items-center gap-1 rounded-lg border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800">
                <Ico d={ICONE_RELOGIO} /> Última compra há {dias} dia(s)
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700">
                <Ico d={ICONE_X} /> Nunca comprou
              </span>
            )}
          </div>
          {(cliente.bairro || cliente.municipio) && (
            <span className="flex items-center gap-1 text-xs text-slate-600">
              <Ico d={ICONE_LOCAL} />
              {[cliente.bairro, cliente.municipio].filter(Boolean).join(' / ')}
            </span>
          )}
          {/* Print dela 08/10: valor da ultima compra e quantos pedidos (sem km). */}
          <div className="grid grid-cols-2 rounded-xl border border-slate-100 bg-slate-50 text-xs">
            <div className="flex min-w-0 items-center gap-2 px-2.5 py-1.5">
              <Ico d={ICONE_CALENDARIO} className="h-5 w-5 shrink-0 text-slate-500" />
              <span className="min-w-0">
                <span data-testid="valor-ultima-compra" className="block break-words font-bold text-slate-800">
                  {resumo ? formatarMoeda(resumo.valorUltima) : '–'}
                </span>
                <span className="block text-slate-500">Última compra</span>
              </span>
            </div>
            <div className="flex min-w-0 items-center gap-2 border-l border-slate-200 px-2.5 py-1.5">
              <Ico d={ICONE_CARRINHO} className="h-5 w-5 shrink-0 text-slate-500" />
              <span>
                <span data-testid="pedidos-do-cliente" className="block font-bold text-slate-800">{resumo?.pedidos ?? 0}</span>
                <span className="block text-slate-500">Pedidos</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {mapsUrl && (
          <a href={mapsUrl} target="_blank" rel="noreferrer" className={BOTAO_CONTORNO}>
            <Ico d={ICONE_LOCAL} className="h-4 w-4 text-red-500" /> Maps
          </a>
        )}
        {wazeUrl && (
          <a href={wazeUrl} target="_blank" rel="noreferrer" className={BOTAO_CONTORNO}>
            <Ico d={ICONE_CARRO} className="h-4 w-4 text-sky-500" /> Waze
          </a>
        )}
        {ligarUrl && (
          <a href={ligarUrl} className={BOTAO_CONTORNO}>
            <Ico d={ICONE_TELEFONE} className="h-4 w-4 text-teal-700" /> Ligar
          </a>
        )}
      </div>
      {ehHoje && (
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => {
              void obterOuCriarPedidoAberto(cliente.id).then((pedido) =>
                navegar(`/pedidos/${pedido.id}`),
              )
            }}
            className="flex items-center justify-center gap-1.5 rounded-xl bg-teal-800 py-2.5 text-sm font-semibold text-white"
          >
            <Ico d={ICONE_DOCUMENTO} className="h-4 w-4" /> Novo pedido
          </button>
          {!visita && (
            <button
              onClick={() => setCheckinAberto(checkinAberto === cliente.id ? null : cliente.id)}
              className="rounded-xl border border-teal-700 bg-white py-2.5 text-sm font-semibold text-teal-800"
            >
              ✓ Não comprou
            </button>
          )}
        </div>
      )}

      {checkinAberto === cliente.id && (
        <div className="border-t border-slate-200 pt-2 space-y-2">
          <select
            aria-label="Motivo"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value as MotivoDaTela)}
            className="w-full border border-slate-300 rounded-lg px-2 py-1.5 text-sm bg-white"
          >
            {MOTIVOS_SEM_PEDIDO.map((m) => (
              <option key={m} value={m}>{ROTULO_MOTIVO[m]}</option>
            ))}
            <option value="remarquei">Não visitei / remarquei</option>
          </select>
          <input
            value={observacao}
            onChange={(e) => setObservacao(e.target.value)}
            placeholder="Observação (opcional)"
            className="w-full border border-slate-300 rounded-lg px-2 py-1.5 text-sm"
          />
          <label className="block text-xs text-slate-600">
            {motivo === 'remarquei' ? 'Nova data de retorno' : 'Marcar retorno para (opcional)'}
            <input
              type="date"
              aria-label="Data de retorno"
              min={amanhaISO}
              value={dataRetorno}
              onChange={(e) => setDataRetorno(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-2 py-1.5 text-sm"
            />
          </label>
          <button
            disabled={!podeConfirmar}
            onClick={() => void aoConfirmarNaoComprou(cliente)}
            className="w-full bg-slate-700 text-white rounded-lg py-1.5 text-sm disabled:opacity-50"
          >
            {motivo === 'remarquei' ? 'Remarcar' : 'Registrar visita sem pedido'}
          </button>
        </div>
      )}
    </div>
  )
}

const BOTAO_CONTORNO =
  'flex items-center justify-center gap-1 rounded-xl border border-slate-300 bg-white py-2 text-sm font-medium text-slate-700'

const ICONE_RELOGIO = 'M12 7v5l3 2M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z'
const ICONE_X = 'M15 9l-6 6M9 9l6 6M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z'
const ICONE_LOCAL = 'M12 21s-7-6.2-7-11.5a7 7 0 1 1 14 0C19 14.8 12 21 12 21zM12 12a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z'
const ICONE_CALENDARIO = 'M4 6h16v14H4zM4 10h16M9 3v4M15 3v4'
const ICONE_CARRINHO = 'M3 4h2l2.4 10.2a1 1 0 0 0 1 .8h8.9a1 1 0 0 0 1-.8L20 8H6M9 19.5h.01M17 19.5h.01'
const ICONE_CARRO = 'M5 17h14l-1.5-5.5a2 2 0 0 0-2-1.5h-7a2 2 0 0 0-2 1.5zM7.5 20h.01M16.5 20h.01'
const ICONE_TELEFONE = 'M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2'
const ICONE_DOCUMENTO = 'M7 3h7l5 5v13H7zM14 3v5h5M10 13h6M10 17h6'

function Ico({ d, className = 'h-3.5 w-3.5 shrink-0' }: { d: string; className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth={2}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={d} />
    </svg>
  )
}

// O desenho do mapa do print: so ilustracao, sem dado de percurso.
function MiniMapa() {
  return (
    <svg viewBox="0 0 64 44" className="h-11 w-16 shrink-0 rounded-lg bg-emerald-50" aria-hidden="true">
      <path d="M0 30 20 22 34 30 64 14" stroke="#d1e7dd" strokeWidth="6" fill="none" />
      <path d="M14 30c6-10 12 4 18-4s10-12 16-14" stroke="#0b4a47" strokeWidth="2" strokeDasharray="3 3" fill="none" />
      <path d="M14 30c-3-4-5-6-5-9a5 5 0 0 1 10 0c0 3-2 5-5 9z" fill="#dc2626" />
      <path d="M48 12c-3-4-5-6-5-9a5 5 0 0 1 10 0c0 3-2 5-5 9z" fill="#dc2626" />
    </svg>
  )
}
